import { PublicErrorCode, STATUS_TO_PUBLIC_CODE } from './codes';

/**
 * Explicit internal-code → public-code overrides.
 *
 * The per-status fallback is deliberately coarse — it exists so an unmapped
 * internal code still renders something sane. An override belongs here when two
 * conditions share an HTTP status but have **different remedies**, because a
 * client that cannot tell them apart cannot act on either: collapsing them
 * leaves `error.message` as the only signal, and parsing prose is exactly what
 * the public catalog exists to make unnecessary.
 */
const INTERNAL_CODE_OVERRIDES: Record<string, string> = {
  // Guard stack (`codes/api.ts`, AA = 00) — finer-grained than the status alone.
  '150001': PublicErrorCode.unauthorized,
  '150002': PublicErrorCode.invalid_api_key,
  '150003': PublicErrorCode.insufficient_scope,
  '150004': PublicErrorCode.workspace_forbidden,
  '150005': PublicErrorCode.rate_limit_exceeded,
  '150006': PublicErrorCode.too_many_requests,
  '150007': PublicErrorCode.api_access_not_in_plan,

  // ── Plan capacity: two 403s, two different remedies ──────────────────────
  //
  // `020002` is the CONNECT cap ("you cannot add more mailboxes at all") and
  // `020007` the WARMUP cap ("you can add it, but pause another one to warm
  // it"). The internal catalog splits them precisely because the remedies
  // differ; routing both through the 403 default put them back together as
  // `forbidden`, so a partner bulk-onboarding a customer had to substring-match
  // our prose to know whether to stop or to pause something.
  '020002': PublicErrorCode.plan_limit_reached,
  '020007': PublicErrorCode.warmup_limit_reached,
  // A third connect-time 403, with a third remedy: resume the subscription.
  //
  // ONE condition, TWO internal codes. `040006` is the general view-only gate
  // (`ApiKeyGuard` rejects every `/v2` call while paused); `020009` is the
  // older connect/reconnect thrower, kept because it has already reached an
  // environment and a client may branch on it. Both must be here — a consumer
  // that learns only one of them misses the other.
  '020009': PublicErrorCode.subscription_paused,
  '040006': PublicErrorCode.subscription_paused,

  // ── Partner headless connect (`codes/email-accounts.ts`, AA = 35) ─────────
  //
  // `023501` would otherwise be `forbidden`, indistinguishable from a scope or
  // workspace rejection, when the actual fix is to register a callback endpoint.
  // `023503` would be `unprocessable_entity`, indistinguishable from a malformed
  // field, when the fix is to send the right mailbox's token.
  //
  // NOT to be confused with `0234xx`, the Microsoft single-mailbox connect
  // block. Those are deliberately absent here: three of the four are CALLBACK
  // outcomes that 302 to a result page and never render an error envelope at
  // all, so a public code for them would describe a response that cannot exist.
  '023501': PublicErrorCode.partner_not_configured,
  '023503': PublicErrorCode.email_mismatch,

  // ── Mailbox send/receive verification (`codes/email-accounts.ts`) ────────
  //
  // `022408` would otherwise be `conflict` and `022409` `invalid_request`,
  // leaving `error.message` as the only way to tell "still checking, wait"
  // from "broken, fix the mailbox". A partner integration hits the pending one
  // routinely: warmup starts on its own after connect, so an early
  // `warmup/enable` lands while the check is still running.
  '022408': PublicErrorCode.mailbox_verification_pending,
  '022409': PublicErrorCode.mailbox_verification_failed,
  // ── Mailbox health re-check (`codes/email-accounts.ts`, AA = 36) ─────────
  //
  // `023601` would otherwise be `rate_limit_exceeded` — the code the guard
  // stack uses for a genuine API throttle (`150005`/`150006`), so an integrator
  // would back off its whole request rate for a limit that is per MAILBOX and
  // measured from that mailbox's last check (the daily automatic one included).
  // `023602` would be a bare `conflict`, indistinguishable from "already
  // connected", when the only remedy is to wait for the run already in flight.
  '023601': PublicErrorCode.recheck_too_soon,
  '023602': PublicErrorCode.check_in_progress,

  // ── Workspace budgets (`codes/settings.ts`) ──────────────────────────────
  //
  // `052001` would otherwise be `invalid_request`, indistinguishable from a
  // malformed body on `POST /v2/workspaces` and `PATCH /v2/workspaces/quotas`,
  // when the payload is valid and the fix is to free budget elsewhere.
  '052001': PublicErrorCode.quota_exceeds_org_limit,
};

/**
 * Translate an internal 6-char error code + HTTP status to a public snake_case
 * code. Explicit guard-stack overrides win; otherwise the HTTP status maps to a
 * default public code; anything unmapped falls back to `internal_error`.
 */
export function toPublicCode(internalCode: string, httpStatus: number): string {
  return (
    INTERNAL_CODE_OVERRIDES[internalCode] ??
    STATUS_TO_PUBLIC_CODE[httpStatus] ??
    PublicErrorCode.internal_error
  );
}
